/**
 * ENHANCED TERRAIN's sea, at the region level: which meshes get the ocean
 * material, the switch that puts it on and takes it off, and the one number a
 * frame moves.
 *
 * `enhanced-structure.ts`'s `structureSwap` in miniature, and deliberately the
 * same shape — a lane reading one will recognise the other. It is a *file* of
 * its own rather than twenty lines inside `world-instancer.ts` because that host
 * is already at aislop's size ceiling, which is the split
 * `world-shared-ref.ts` was taken at.
 *
 * ## The swap is an assignment, and the off state is the object it swaps back to
 *
 * Both materials are built at region load and both stay built, so a flip costs
 * one property write and no pipeline compile — the arrangement
 * `landcover-plate-colours.ts` has for the plates' two colour buffers and
 * `enhanced-structure.ts` has for the buildings' two geometries.
 * `scene-boot.ts` states the setting once *before* the precompile, so a player
 * who turns the sea on mid-sortie is not waiting on a shader.
 *
 * And the off state is not "the ocean material with a flag": it is the very
 * `MeshBasicMaterial` instance the rest of the region is drawn with, which is
 * what makes "ENHANCED TERRAIN off is today's pixels" a property of the object
 * graph rather than a claim about a shader's arithmetic.
 * `ocean-toggle.test.ts` asserts that identity.
 *
 * ## Why the frame update is here and not on a material
 *
 * The sea is animated on the **encounter's** clock (`ocean-waves.ts`), and the
 * only place that has it is the frame loop. Routing `setFrame` through the
 * region rather than reaching into the material from `scene-draw.ts` keeps the
 * one rule this feature must not break — *nothing about the sea moves on the
 * wall clock* — expressible in a single call site, which is the call
 * `ocean-pose-freeze.test.ts` checks.
 */

import type * as THREE from "three/webgpu";
import { createOceanMaterial, type OceanMaterial } from "./ocean-material.ts";
import type { OceanLook } from "../flight/ocean-look.ts";
import type { ShoreField } from "../world/shore-field.ts";

/** What a region needs to draw a sea at all. Absent for the three inland maps, which place no `s = 10` plate. */
export interface OceanSource {
  readonly look: OceanLook;
  readonly shore: ShoreField;
}

export interface OceanSwap {
  /** Meshes carrying open sea — 1 on London and Dunkirk, 0 everywhere else. Reported so a caller can say the toggle has nothing to show rather than guess. */
  readonly plateCount: number;
  /** ENHANCED TERRAIN. Idempotent; a no-op when the region has no sea. */
  readonly set: (enabled: boolean) => void;
  /** One frame's clock and anchor (`ocean-material.ts`'s `setFrame`). A no-op while the switch is off or the region is dry. */
  readonly setFrame: (timeS: number, anchorX: number, anchorZ: number) => void;
  /**
   * Releases the material and its two textures.
   *
   * **Nothing in `/fly` calls it, deliberately, and that is worth saying rather
   * than leaving to be discovered.** `dispose-tree.ts` frees a material by
   * walking the scene graph, and with the switch *off* the ocean material is
   * attached to no mesh — so it would be missed. What actually releases it is
   * `flight-scene.ts` ending a sortie on `renderer.dispose()`, which takes every
   * GPU object with it; the sea's is 1 MB of shoal texture and one pipeline, and
   * `/fly` builds exactly one region per sortie. This exists for a caller that
   * ever wants to drop a region *without* dropping the renderer — the `/assets`
   * world pane's retained-region cache is the shape that would — and
   * `ocean-toggle.test.ts` exercises it so it cannot rot.
   */
  readonly dispose: () => void;
}

/** A region with no sea: every method is a no-op and nothing was built. */
export const NO_OCEAN: OceanSwap = {
  plateCount: 0,
  set: () => undefined,
  setFrame: () => undefined,
  dispose: () => undefined,
};

/**
 * Binds the ocean material to every sea plate the region placed.
 *
 * The arguments are `structureSwap`'s, ref for ref: the region's entries in
 * `.WLD` record order, what each ref loaded to, and the meshes keyed by ref. The
 * plates are picked out by `SharedRef.isSeaPlate` here rather than by the host,
 * so `world-instancer.ts` gains one call and no bookkeeping. `flat` is the
 * shared `MeshBasicMaterial` every one of them was built with — the object the
 * off state returns to.
 */
export function oceanSwap(
  entries: readonly (readonly [string, readonly unknown[]])[],
  loaded: readonly ({ readonly isSeaPlate: boolean } | undefined)[],
  meshes: ReadonlyMap<string, THREE.InstancedMesh>,
  flat: THREE.Material,
  source: OceanSource | undefined,
): OceanSwap {
  const seaMeshes = entries
    .map(([objectRef], slot) => (loaded[slot]?.isSeaPlate === true ? meshes.get(objectRef) : undefined))
    .filter((mesh): mesh is THREE.InstancedMesh => mesh !== undefined);
  if (!source || seaMeshes.length === 0) return NO_OCEAN;
  let ocean: OceanMaterial | undefined;
  let enabledNow = false;
  return {
    plateCount: seaMeshes.length,
    set: (enabled: boolean) => {
      enabledNow = enabled;
      // Built on the first request rather than at load, for
      // `scene-terrain.ts`'s reason: a player who never turns ENHANCED TERRAIN
      // on should not pay for a 1 MB shoal texture or a second pipeline. The
      // boot states the setting before the precompile, so the default-on case
      // still compiles in `PRE-FLIGHT CHECKS` and not on a frame.
      if (enabled) ocean ??= createOceanMaterial(source.look, source.shore);
      const material = enabled && ocean ? ocean.material : flat;
      for (const mesh of seaMeshes) mesh.material = material;
    },
    setFrame: (timeS: number, anchorX: number, anchorZ: number) => {
      if (enabledNow) ocean?.setFrame(timeS, anchorX, anchorZ);
    },
    dispose: () => ocean?.dispose(),
  };
}
